import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";
import { AIActor } from "../types/actor";
import api from "../services/api";
import {
  transformImageUrl,
  setupCrossOrigin,
  getImageLoadErrorMessage,
} from "../utils/imageUtils";
import PhotoEditor from "../PhotoEditor";
import TutorialDialog from "./TutorialDialog";

interface ProjectFile {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size?: string;
  created_at: string;
}

// Add a project interface to include product_image_url
interface ProjectDetails {
  id: string;
  name: string;
  product_image_url?: string;
  created_at: string;
  updated_at: string;
}

interface CharacterCanvasDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedActors: AIActor[];
  productImage?: ProjectFile | null;
  productPrompt?: string;
  projectId: string;
  onMaskGenerated?: (actorId: string) => void;
}

interface ProductPosition {
  scale: { x: number; y: number };
  position: { x: number; y: number };
  rotation: number;
}

// Store positions by actor ID
interface ProductPositions {
  [actorId: string]: ProductPosition;
}

// Store mask URLs by actor ID
interface ProductMasks {
  [actorId: string]: {
    mask_url: string;
    mask_id: string;
  };
}

// Interface for merged image data
interface MergedImage {
  merged_image_id: string;
  merged_image_url: string;
  actor_id: string;
  project_id: string;
  position_x: number;
  position_y: number;
  scale_x: number;
  scale_y: number;
  rotation: number;
}

// Store merged image URLs by actor ID
interface MergedImages {
  [actorId: string]: MergedImage;
}

// Helper function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy text: ", err);
    return false;
  }
};

export default function CharacterCanvasDialog({
  isOpen,
  onClose,
  selectedActors,
  productImage,
  productPrompt,
  projectId,
  onMaskGenerated,
}: CharacterCanvasDialogProps) {
  const [currentActorIndex, setCurrentActorIndex] = useState(0);
  const [productPositions, setProductPositions] = useState<ProductPositions>(
    {}
  );
  const [avatarData, setAvatarData] = useState<
    Record<string, { x: number; y: number; width: number; height: number }>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editedActors, setEditedActors] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isProductCoveringMask, setIsProductCoveringMask] = useState(true);
  // Add a state to track whether we're in positioning mode or preview mode
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Add state for storing generated mask URLs
  const [productMasks, setProductMasks] = useState<ProductMasks>({});
  // Add state for mask generation status
  const [isMaskGenerating, setIsMaskGenerating] = useState(false);
  // Add state to track which URL was just copied
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  // Add state for merged images
  const [mergedImages, setMergedImages] = useState<MergedImages>({});
  const [isMergeLoading, setIsMergeLoading] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(0);
  const [mergeError, setMergeError] = useState<string | null>(null);

  // Track unsaved changes
  const currentTransformRef = useRef<Record<string, ProductPosition>>({});
  const hasUnsavedChangesRef = useRef<boolean>(false);
  const nextActorIndexRef = useRef<number | null>(null);
  // Store coverage status in a ref to avoid unnecessary re-renders
  const coverageStatusRef = useRef<boolean>(true);
  // Reference to PhotoEditor instance for mask generation
  const photoEditorRef = useRef<any>(null);
  // Reference to merge progress interval
  const mergeProgressIntervalRef = useRef<number | null>(null);

  // Add a state to track position changes after going back from preview mode
  const [positionChangedAfterPreview, setPositionChangedAfterPreview] =
    useState(false);

  // Add a reference to store original positions for comparison
  const originalPositionsRef = useRef<Record<string, ProductPosition>>({});

  // Add state for project details
  const [projectDetails, setProjectDetails] = useState<ProjectDetails | null>(
    null
  );

  const currentActor = selectedActors[currentActorIndex] || null;
  const totalActors = selectedActors.length;
  const isLastActor = currentActorIndex === totalActors - 1;
  const isFirstActor = currentActorIndex === 0;
  const currentActorId = currentActor?.id || "";
  const currentProductPosition =
    currentActor && productPositions[currentActor.id];
  const currentAvatarData = currentActor && avatarData[currentActor.id];
  const currentMaskData = currentActor && productMasks[currentActor.id];
  const currentMergedImage = currentActor && mergedImages[currentActor.id];

  // Preload next actor's data when current actor is loaded
  useEffect(() => {
    if (!isOpen || isLoading || !currentActor || isTransitioning) return;

    // If there's a next actor, preload its data
    if (!isLastActor) {
      const nextActor = selectedActors[currentActorIndex + 1];
      if (nextActor) {
        loadActorPosition(nextActor.id, false);
      }
    }
  }, [
    isOpen,
    currentActor,
    currentActorIndex,
    isLoading,
    isLastActor,
    isTransitioning,
  ]);

  // Add a function to compare positions to see if they've actually changed
  const hasPositionChanged = (
    position: ProductPosition,
    mergedImage: MergedImage
  ): boolean => {
    if (!position || !mergedImage) {
      // If we don't have both positions to compare, assume it has changed
      return true;
    }

    // Tolerance values to account for tiny floating-point differences
    const positionTolerance = 0.5;
    const scaleTolerance = 0.01;
    const rotationTolerance = 0.5;

    // Deep comparison of position objects
    const positionChanged =
      Math.abs(position.position.x - mergedImage.position_x) >
        positionTolerance ||
      Math.abs(position.position.y - mergedImage.position_y) >
        positionTolerance ||
      Math.abs(position.scale.x - mergedImage.scale_x) > scaleTolerance ||
      Math.abs(position.scale.y - mergedImage.scale_y) > scaleTolerance ||
      Math.abs(position.rotation - mergedImage.rotation) > rotationTolerance;

    console.log("Position comparison:", {
      currentPosition: {
        x: position.position.x,
        y: position.position.y,
        scaleX: position.scale.x,
        scaleY: position.scale.y,
        rotation: position.rotation,
      },
      savedPosition: {
        x: mergedImage.position_x,
        y: mergedImage.position_y,
        scaleX: mergedImage.scale_x,
        scaleY: mergedImage.scale_y,
        rotation: mergedImage.rotation,
      },
      changed: positionChanged,
    });

    return positionChanged;
  };

  // Load actor position data
  const loadActorPosition = async (actorId: string, setLoadingState = true) => {
    if (setLoadingState) {
      setIsLoading(true);
    }

    try {
      // First, fetch the avatar data for default position coordinates
      try {
        const avatarResponse = await api.get(`/api/avatars/${actorId}`);
        if (avatarResponse.data && avatarResponse.data.avatar) {
          const avatar = avatarResponse.data.avatar;
          setAvatarData((prev) => ({
            ...prev,
            [actorId]: {
              x: avatar.x,
              y: avatar.y,
              width: avatar.width,
              height: avatar.height,
              voice_id: avatar.voice_id,
            },
          }));
          console.log(`Loaded avatar data for actor ${actorId}:`, avatar);
        }
      } catch (avatarError) {
        console.error(
          `Error fetching avatar data for actor ${actorId}:`,
          avatarError
        );
      }

      // Try to get product position if it exists
      try {
        console.log(
          `Fetching product position for actor ${actorId} in project ${projectId}`
        );
        const productResponse = await api.get(
          `/api/projects/product-position/${actorId}/${projectId}`
        );

        // Check if the response has the not_found flag
        if (
          productResponse.data &&
          !productResponse.data.not_found &&
          productResponse.data.position
        ) {
          console.log(
            `Received product position for actor ${actorId} in project ${projectId}:`,
            productResponse.data.position
          );

          // Store the position in state for UI
          setProductPositions((prev) => ({
            ...prev,
            [actorId]: productResponse.data.position,
          }));

          // Also store in the transform ref for future saves
          currentTransformRef.current[actorId] = productResponse.data.position;

          // Store original position for comparison
          originalPositionsRef.current[actorId] = JSON.parse(
            JSON.stringify(productResponse.data.position)
          );
        } else if (productResponse.data && productResponse.data.not_found) {
          console.log(
            `No saved position found for actor ${actorId} in project ${projectId}, will use avatar coordinates`
          );

          // Create a default position based on avatar data
          if (avatarData[actorId]) {
            const avatarCoords = avatarData[actorId];
            console.log(
              `Using avatar coordinates for initial position:`,
              avatarCoords
            );

            // Calculate a sensible default scale and position
            const defaultScale = 0.5; // Default scale, can be adjusted
            const defaultPosition = {
              position: {
                x: avatarCoords.x,
                y: avatarCoords.y,
              },
              scale: {
                x: defaultScale,
                y: defaultScale,
              },
              rotation: 0,
            };

            // Store this default position in the transform ref
            currentTransformRef.current[actorId] = defaultPosition;

            // Also update UI state
            setProductPositions((prev) => ({
              ...prev,
              [actorId]: defaultPosition,
            }));

            console.log(
              `Set default position for actor ${actorId}:`,
              defaultPosition
            );

            // Mark as unsaved so it will be saved when user navigates
            if (currentActorId === actorId) {
              hasUnsavedChangesRef.current = true;
            }
          }
        } else {
          console.log(
            `No valid position data received for actor ${actorId} in project ${projectId}`
          );
        }
      } catch (error: any) {
        // Log the error but don't fail - the frontend will use the avatar coordinates
        console.warn(
          `Error fetching product position for actor ${actorId} in project ${projectId}:`,
          error?.response?.status
        );
        if (error?.response?.status === 404) {
          console.log(
            `No saved product position for actor ${actorId} in project ${projectId}, will use avatar coordinates`
          );
          // Set hasUnsavedChangesRef to true to ensure the default position gets saved
          // even if the user doesn't move the product
          if (currentActorId === actorId) {
            console.log(
              `Setting unsaved changes flag for first-time actor ${actorId}`
            );
            hasUnsavedChangesRef.current = true;
          }
        } else {
          console.error(`Unexpected error fetching product position:`, error);
        }
      }

      // Try to get existing merged image if it exists
      try {
        const mergedImageResponse = await api.get(
          `/api/avatars/merged-image/${actorId}/${projectId}`
        );
        if (mergedImageResponse.data && !mergedImageResponse.data.not_found) {
          console.log(
            `Found existing merged image for actor ${actorId}:`,
            mergedImageResponse.data
          );
          setMergedImages((prev) => ({
            ...prev,
            [actorId]: {
              merged_image_id: mergedImageResponse.data.merged_image_id,
              merged_image_url: mergedImageResponse.data.merged_image_url,
              actor_id: actorId,
              project_id: projectId,
              position_x: mergedImageResponse.data.position_x,
              position_y: mergedImageResponse.data.position_y,
              scale_x: mergedImageResponse.data.scale_x,
              scale_y: mergedImageResponse.data.scale_y,
              rotation: mergedImageResponse.data.rotation,
            },
          }));
        }
      } catch (error) {
        // Merged image doesn't exist yet, which is fine
        console.log(`No existing merged image found for actor ${actorId}`);
      }

      return true;
    } catch (error) {
      console.error(
        `Error fetching position data for actor ${actorId}:`,
        error
      );
      if (setLoadingState) {
        setError("Failed to load position data");
      }
      return false;
    } finally {
      if (setLoadingState) {
        setIsLoading(false);
      }
    }
  };

  // Load the current actor's position when the current index changes
  useEffect(() => {
    const fetchCurrentActorPosition = async () => {
      if (!isOpen || !currentActor) return;

      // Always load the product position for the current actor
      setIsLoading(true);
      await loadActorPosition(currentActor.id);
      setIsLoading(false);
    };

    fetchCurrentActorPosition();
  }, [isOpen, currentActor]);

  // Handle navigation to next actor with loading state
  const handleNavigation = async (nextIndex: number) => {
    // Save current actor position before navigating
    if (hasUnsavedChangesRef.current && currentActorId) {
      setIsSaving(true);
      await saveCurrentActorChanges();
      setIsSaving(false);
    }

    // Set transitioning state to prevent flicker
    setIsTransitioning(true);

    // Reset preview mode when navigating to a new actor
    setIsPreviewMode(false);

    // First check if we have the data for the next actor
    const nextActor = selectedActors[nextIndex];

    if (nextActor) {
      if (!productPositions[nextActor.id]) {
        // We need to load the next actor's data
        setIsLoading(true);
        const success = await loadActorPosition(nextActor.id);
        if (!success) {
          setIsTransitioning(false);
          return;
        }
      }

      // Now actually change the index
      setCurrentActorIndex(nextIndex);
    }

    // Clear transitioning state after a small delay to ensure smooth transition
    setTimeout(() => {
      setIsTransitioning(false);
    }, 50);
  };

  // Reset current actor index when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCurrentActorIndex(0);
      setEditedActors(new Set());
      // Clear positions to avoid showing stale data
      setProductPositions({});
      setAvatarData({});
      setProductMasks({});
      setIsLoading(true);
      // Reset preview mode
      setIsPreviewMode(false);
    }
  }, [isOpen]);

  // Handle position change with optimized state updates
  const handlePositionChange = useCallback(
    (position: ProductPosition) => {
      if (!currentActorId) {
        console.warn("No current actor ID available for position change");
        return;
      }

      // Track the change locally without saving to server yet
      currentTransformRef.current[currentActorId] = position;
      hasUnsavedChangesRef.current = true;

      // If we're in positioning mode after having been in preview mode, mark that position has changed
      if (!isPreviewMode && productMasks[currentActorId]) {
        setPositionChangedAfterPreview(true);
      }

      // Update the UI state immediately so the user sees their changes
      setProductPositions((prev) => ({
        ...prev,
        [currentActorId]: position,
      }));
    },
    [currentActorId, isPreviewMode, productMasks]
  );

  // Handle coverage status change with debouncing to prevent too many re-renders
  const handleCoverageChange = useCallback((isCovered: boolean) => {
    // Only update state if the coverage status actually changed
    if (coverageStatusRef.current !== isCovered) {
      coverageStatusRef.current = isCovered;
      setIsProductCoveringMask(isCovered);
    }
  }, []);

  // Generate and upload a product mask for the current actor
  const generateProductMask = async () => {
    if (!currentActor || !photoEditorRef.current) {
      console.error(
        "Cannot generate mask: no current actor or PhotoEditor reference"
      );
      return null;
    }

    try {
      setIsMaskGenerating(true);
      console.log(
        `Generating product mask for actor ${currentActor.id} in project ${projectId}`
      );

      // Use the PhotoEditor's generateProductMask method to create and upload the mask
      const maskResult = await photoEditorRef.current.generateProductMask(
        currentActor.id,
        projectId,
        api
      );

      if (maskResult) {
        console.log("Mask generated and uploaded successfully:", maskResult);
        // Store the mask URL in state
        setProductMasks((prev) => ({
          ...prev,
          [currentActor.id]: {
            mask_url: maskResult.mask_url,
            mask_id: maskResult.mask_id,
          },
        }));

        // Call the onMaskGenerated callback if provided
        if (onMaskGenerated && currentActor) {
          console.log(
            `Calling onMaskGenerated callback for actor ${currentActor.id}`
          );
          onMaskGenerated(currentActor.id);
        }

        return maskResult;
      } else {
        console.error("Failed to generate or upload mask");
        return null;
      }
    } catch (error) {
      console.error("Error generating product mask:", error);
      return null;
    } finally {
      setIsMaskGenerating(false);
    }
  };

  // Save all pending changes for the current actor
  const saveCurrentActorChanges = async () => {
    if (!currentActorId || !hasUnsavedChangesRef.current) return;

    const position = currentTransformRef.current[currentActorId];
    if (!position) {
      console.error(`No position data found for actor ${currentActorId}`);
      return;
    }
    console.log("Position:", position);

    console.log(
      `Saving product position for actor ${currentActorId} in project ${projectId}:`,
      position
    );

    try {
      const response = await api.post(
        `/api/projects/product-position/${currentActorId}/${projectId}`,
        { position }
      );
      console.log(
        `Successfully saved product position for actor ${currentActorId} in project ${projectId}:`,
        response.data
      );
      hasUnsavedChangesRef.current = false;

      // Update the stored positions
      setProductPositions((prev) => ({
        ...prev,
        [currentActorId]: position,
      }));

      // Mark this actor as edited
      setEditedActors((prev) => {
        const updated = new Set(prev);
        updated.add(currentActorId);
        return updated;
      });
    } catch (error) {
      console.error("Error saving product position:", error);
      alert("Failed to save product position. Please try again.");
    }
  };

  // Add a new function to handle URL copying
  const handleCopyUrl = async (url: string) => {
    const success = await copyToClipboard(url);
    if (success) {
      setCopiedUrl(url);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedUrl(null);
      }, 2000);
    }
  };

  // Function to generate product fill image
  const generateProductFillImage = async () => {
    if (!photoEditorRef.current || !currentActor) {
      console.error("PhotoEditor reference or current actor not available");
      return null;
    }

    try {
      // setIsMaskGenerating(true);
      console.log(
        `Generating product fill image for actor ${currentActor.id} in project ${projectId}`
      );

      // Use the PhotoEditor's generateProductFillImage method to create and upload the fill image
      const fillImageResult =
        await photoEditorRef.current.generateProductFillImage(
          currentActor.id,
          projectId,
          api
        );

      if (fillImageResult) {
        console.log(
          "Product fill image generated and uploaded successfully:",
          fillImageResult
        );

        return {
          file_url: (fillImageResult.file_url as string) || "",
          file_id: (fillImageResult.file_id as string) || "",
        };
        // Store the fill image URL in state and show the product fill view
        // setProductFillImage(fillImageResult.file_url);
        // setShowProductFill(true);
      } else {
        console.error("Failed to generate or upload product fill image");
        return null;
      }
    } catch (error) {
      console.error("Error generating product fill image:", error);
      return null;
    } finally {
      // setIsMaskGenerating(false);
    }
  };

  // Toggle between positioning and preview modes
  const togglePreviewMode = async () => {
    if (!isPreviewMode) {
      // Save changes before showing preview

      if (hasUnsavedChangesRef.current && currentActorId) {
        console.log("Saving current actor changes");
        setIsSaving(true);
        await saveCurrentActorChanges();
        setIsSaving(false);
      }

      // Get the current position and any existing merged image
      let currentPosition = currentTransformRef.current[currentActorId];

      // If we don't have a position but have avatar data, create a default position
      if (!currentPosition && avatarData[currentActorId]) {
        console.log(
          `No position data for merge, creating default based on avatar coordinates`
        );
        const avatarCoords = avatarData[currentActorId];
        const defaultScale = 0.5; // Default scale

        // Create a default position
        currentPosition = {
          position: {
            x: avatarCoords.x,
            y: avatarCoords.y,
          },
          scale: {
            x: defaultScale,
            y: defaultScale,
          },
          rotation: 0,
        };

        // Store in the ref for future use
        currentTransformRef.current[currentActorId] = currentPosition;
        console.log(
          `Created default position for image merge:`,
          currentPosition
        );

        // Also mark as unsaved so it will be saved on next navigation
        hasUnsavedChangesRef.current = true;
      }

      if (!currentPosition) {
        console.error("Cannot merge images: no position data available");
        setMergeError("Missing position data for image merging");
        return;
      }

      // Check if position has changed compared to the saved merged image
      const existingMergedImage = currentActorId
        ? mergedImages[currentActorId]
        : null;
      const positionChanged = existingMergedImage
        ? hasPositionChanged(currentPosition, existingMergedImage)
        : true;

      console.log(
        "Position changed compared to saved merged image?",
        positionChanged
      );
      console.log("Current position:", currentPosition);
      console.log("Existing merged image:", existingMergedImage);

      // Generate and upload mask before showing preview
      // const uploadedFillImage = await generateProductFillImage();
      let maskData: { mask_url: string; mask_id: string } | null = null;

      // We need a new mask if:
      // 1. We don't have a mask yet, or
      // 2. The position has changed since the original position
      const needNewMask = !productMasks[currentActorId] || positionChanged;

      if (currentActorId && needNewMask) {
        console.log(
          "Generating new mask because:",
          !productMasks[currentActorId]
            ? "No existing mask"
            : "Position has changed"
        );
        // maskData = await generateProductFillImage();
        // setIsMaskGenerating(true);
        maskData = await generateProductMask();
        // setIsMaskGenerating(false);
        if (!maskData) {
          alert("Could not generate product mask. Please try again.");
          return;
        }
      } else if (currentActorId && productMasks[currentActorId]) {
        // If we already have mask data in state, use it
        console.log("Using existing mask - position hasn't changed");
        maskData = productMasks[currentActorId];
      }

      // Enter preview mode
      setIsPreviewMode(true);

      // Generate merged image if:
      // 1. Position has changed from the saved merged image, or
      // 2. We don't have a merged image yet
      if (
        currentActorId &&
        maskData &&
        (positionChanged || !existingMergedImage)
      ) {
        console.log("Mask data:", maskData);
        // Pass the mask data directly to mergeImages
        mergeImages(maskData as any);
      } else {
        console.log("Using existing merged image - position hasn't changed");
      }
    } else {
      // Exit preview mode, back to positioning
      setIsPreviewMode(false);
    }
  };

  // Fetch project details when dialog opens
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!isOpen || !projectId) return;

      try {
        const response = await api.get(`/api/projects/${projectId}`);
        if (response.data) {
          setProjectDetails(response.data);
          console.log("Fetched project details:", response.data);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };

    fetchProjectDetails();
  }, [isOpen, projectId]);

  // Add a new useEffect to log projectDetails changes
  useEffect(() => {
    if (projectDetails) {
      console.log("ProjectDetails updated:", projectDetails);
      console.log(
        "Project product_image_url:",
        projectDetails.product_image_url
      );
    }
  }, [projectDetails]);

  // Fetch project details directly before merging images
  const fetchProjectDetailsDirectly = async () => {
    if (!projectId) return null;

    try {
      const response = await api.get(`/api/projects/${projectId}`);
      if (response.data && response.data.product_image_url) {
        return response.data.product_image_url;
      }
    } catch (error) {
      console.error("Error directly fetching project details:", error);
    }
    return null;
  };

  // Determine which product image URL to use
  const getProductImageUrl = () => {
    // First priority: Use project.product_image_url if available
    if (projectDetails?.product_image_url) {
      const url = projectDetails.product_image_url;
      console.log("Using product_image_url from project:", url);

      // Validate the URL
      if (!url || typeof url !== "string" || url.trim() === "") {
        console.error("Invalid product_image_url from project", url);
        return undefined; // Return undefined instead of null for type compatibility
      }

      return url;
    }

    // Second priority: Use provided productImage prop
    if (productImage?.file_url) {
      const url = productImage.file_url;
      console.log("Using productImage prop:", url);

      // Validate the URL
      if (!url || typeof url !== "string" || url.trim() === "") {
        console.error("Invalid file_url from productImage prop", url);
        return undefined; // Return undefined instead of null for type compatibility
      }

      return url;
    }

    console.log("No product image URL available");
    return undefined; // Return undefined instead of null for type compatibility
  };

  // Add a function to merge images using the new API endpoint
  const mergeImages = async (maskData: {
    file_url: string;
    file_id: string;
  }) => {
    if (!currentActor) {
      console.error("Cannot merge images: missing actor");
      setMergeError("Missing required data for image merging");
      return null;
    }

    // Try to get product image URL from both sources
    const cachedProductImageUrl = getProductImageUrl();
    const directProductImageUrl = await fetchProjectDetailsDirectly();

    // Choose the best available URL
    let productImageUrl = directProductImageUrl || cachedProductImageUrl;

    console.log("Chosen product image URL:", productImageUrl);
    console.log("From direct fetch:", directProductImageUrl);
    console.log("From cached state:", cachedProductImageUrl);

    // Enhanced validation for product image URL
    if (productImageUrl === undefined || productImageUrl === null) {
      console.error("Cannot merge images: missing product image URL");
      setMergeError("Missing or invalid product image URL");
      return null;
    }

    if (typeof productImageUrl !== "string" || productImageUrl.trim() === "") {
      console.error("Invalid product image URL:", productImageUrl);
      setMergeError("Product image URL is invalid");
      return null;
    }

    // Log product image URL source for debugging
    if (projectDetails?.product_image_url === productImageUrl) {
      console.log("Using URL from project.product_image_url");
    } else if (productImage?.file_url === productImageUrl) {
      console.log("Using URL from productImage.file_url");
    } else {
      console.log("Using URL from unknown source");
    }

    // Get the current position
    let currentPosition = currentTransformRef.current[currentActor.id];

    // If we don't have a position but have avatar data, create a default position
    if (!currentPosition && avatarData[currentActor.id]) {
      console.log(
        `No position data for merge, creating default based on avatar coordinates`
      );
      const avatarCoords = avatarData[currentActor.id];
      const defaultScale = 0.5; // Default scale

      // Create a default position
      currentPosition = {
        position: {
          x: avatarCoords.x,
          y: avatarCoords.y,
        },
        scale: {
          x: defaultScale,
          y: defaultScale,
        },
        rotation: 0,
      };

      // Store in the ref for future use
      currentTransformRef.current[currentActor.id] = currentPosition;
      console.log(`Created default position for image merge:`, currentPosition);

      // Also mark as unsaved so it will be saved on next navigation
      hasUnsavedChangesRef.current = true;
    }

    if (!currentPosition) {
      console.error("Cannot merge images: no position data available");
      setMergeError("Missing position data for image merging");
      return null;
    }

    try {
      setIsMergeLoading(true);
      setMergeProgress(0);
      setMergeError(null);

      // Start a progress animation while waiting for the merge
      // This is just for UI feedback and doesn't reflect actual progress
      if (mergeProgressIntervalRef.current) {
        window.clearInterval(mergeProgressIntervalRef.current);
      }

      mergeProgressIntervalRef.current = window.setInterval(() => {
        setMergeProgress((prev) => {
          // Simulate progress up to 90% (we'll set to 100% when actually done)
          if (prev < 90) {
            // Increase by smaller increments to make it take ~6-7 seconds to reach 90%
            // 90% / (6500ms / 300ms) ≈ 4.15% per interval
            return prev + (Math.random() * 1.5 + 3);
          }
          return prev;
        });
      }, 300);

      // Create the payload
      const payload = {
        avatar_image_url: currentActor.image,
        product_image_url: productImageUrl,
        mask_url: maskData.file_url,
        mask_id: maskData.file_id,
        actor_id: currentActor.id,
        project_id: projectId,
        position_x: currentPosition.position.x,
        position_y: currentPosition.position.y,
        scale_x: currentPosition.scale.x,
        scale_y: currentPosition.scale.y,
        rotation: currentPosition.rotation,
      };

      // Log the full request payload
      console.log(
        "Merge image request payload:",
        JSON.stringify(payload, null, 2)
      );

      // Log the product image URL specifically
      console.log("Product image URL being sent:", productImageUrl);
      console.log(
        "Product image URL from project:",
        projectDetails?.product_image_url
      );
      console.log("Product image URL from prop:", productImage?.file_url);

      // Validate that all required fields have proper values
      for (const [key, value] of Object.entries(payload)) {
        if (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        ) {
          const errorMsg = `Invalid value for ${key}: ${value}`;
          console.error(errorMsg);
          setMergeError(errorMsg);

          // Stop loading and clean up
          if (mergeProgressIntervalRef.current) {
            window.clearInterval(mergeProgressIntervalRef.current);
            mergeProgressIntervalRef.current = null;
          }
          setIsMergeLoading(false);

          return null;
        }
      }

      // Call the API to merge the images
      const response = await api.post("/api/avatars/merge-image", payload);

      // Stop the progress interval
      if (mergeProgressIntervalRef.current) {
        window.clearInterval(mergeProgressIntervalRef.current);
        mergeProgressIntervalRef.current = null;
      }

      // Set progress to 100% to indicate completion
      setMergeProgress(100);

      if (response.data) {
        console.log("Merge completed successfully:", response.data);
        // Store the merged image URL in state
        setMergedImages((prev) => ({
          ...prev,
          [currentActor.id]: response.data,
        }));

        // Call the onMaskGenerated callback if provided
        if (onMaskGenerated && currentActor) {
          console.log(
            `Calling onMaskGenerated callback for actor ${currentActor.id} after merge`
          );
          onMaskGenerated(currentActor.id);
        }

        return response.data;
      } else {
        setMergeError("Failed to merge images: No response data");
        return null;
      }
    } catch (error: any) {
      // Add explicit type to avoid TypeScript error
      console.error("Error merging images:", error);

      // Log detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        console.error("Error response headers:", error.response.headers);

        // Enhanced error message with more details
        setMergeError(
          `Error ${error.response.status}: ${JSON.stringify(
            error.response.data
          )}`
        );
      } else if (error.request) {
        // The request was made but no response was received
        console.error("Error request:", error.request);
        setMergeError("Request was made but no response received");
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error message:", error.message);
        setMergeError(`Request error: ${error.message}`);
      }

      return null;
    } finally {
      // Ensure progress interval is cleared
      if (mergeProgressIntervalRef.current) {
        window.clearInterval(mergeProgressIntervalRef.current);
        mergeProgressIntervalRef.current = null;
      }
      setIsMergeLoading(false);
    }
  };

  // Handle confirmation of positioning and move to the next actor
  const handleConfirmPosition = async () => {
    // Mark current actor as edited
    if (currentActorId) {
      setEditedActors((prev) => {
        const updated = new Set(prev);
        updated.add(currentActorId);
        return updated;
      });
    }

    if (isLastActor) {
      // We're done with all actors
      onClose();
    } else {
      // Move to the next actor
      await handleNavigation(currentActorIndex + 1);
    }
  };

  // Handle navigation through avatars
  const handleNext = async () => {
    if (isPreviewMode) {
      // If in preview mode, confirm and proceed
      await handleConfirmPosition();
    } else {
      // If in positioning mode, save changes and show preview
      if (currentActorId) {
        // Check if we need to save changes
        if (hasUnsavedChangesRef.current) {
          setIsSaving(true);
          await saveCurrentActorChanges();
          setIsSaving(false);
        }
        // Special case: Check if we have a saved position in ProductPositions state
        // If not, we should save the current position even if hasUnsavedChangesRef is false
        else if (
          !productPositions[currentActorId] &&
          currentTransformRef.current[currentActorId]
        ) {
          console.log(
            `No saved position found for actor ${currentActorId}, saving default position...`
          );
          setIsSaving(true);
          hasUnsavedChangesRef.current = true; // Set this to true to allow saving
          await saveCurrentActorChanges();
          setIsSaving(false);
        }
      }
      togglePreviewMode();
    }
  };

  const handlePrevious = async () => {
    if (isPreviewMode) {
      // If in preview mode, go back to positioning
      setIsPreviewMode(false);
      // Reset position changed flag so we know if it changes again
      setPositionChangedAfterPreview(false);
    } else if (!isFirstActor) {
      // If in positioning mode and not the first actor, go to previous actor
      // Save current changes before going back
      if (currentActorId) {
        if (hasUnsavedChangesRef.current) {
          setIsSaving(true);
          await saveCurrentActorChanges();
          setIsSaving(false);
        }
        // Special case: Check if we have a saved position in ProductPositions state
        // If not, we should save the current position even if hasUnsavedChangesRef is false
        else if (
          !productPositions[currentActorId] &&
          currentTransformRef.current[currentActorId]
        ) {
          console.log(
            `No saved position found for actor ${currentActorId}, saving default position...`
          );
          setIsSaving(true);
          hasUnsavedChangesRef.current = true; // Set this to true to allow saving
          await saveCurrentActorChanges();
          setIsSaving(false);
        }
      }
      await handleNavigation(currentActorIndex - 1);
    }
  };

  const handleSkip = () => {
    if (isPreviewMode) {
      // If in preview mode, skip to next actor
      if (isLastActor) {
        onClose();
      } else {
        handleNavigation(currentActorIndex + 1);
      }
    } else {
      // If in positioning mode, skip to preview or next actor
      // Don't save on skip, just navigate
      if (isLastActor) {
        onClose();
      } else {
        // Reset any unsaved changes for the current actor
        hasUnsavedChangesRef.current = false;
        // Show preview or go to next actor
        togglePreviewMode();
      }
    }
  };

  // Handle dialog close with unsaved changes
  const handleDialogClose = async () => {
    // Save any pending changes before closing
    if (currentActorId) {
      if (hasUnsavedChangesRef.current) {
        setIsSaving(true);
        await saveCurrentActorChanges();
        setIsSaving(false);
      }
      // Special case: Check if we have a saved position in ProductPositions state
      // If not, we should save the current position even if hasUnsavedChangesRef is false
      else if (
        !productPositions[currentActorId] &&
        currentTransformRef.current[currentActorId]
      ) {
        console.log(
          `No saved position found for actor ${currentActorId}, saving default position...`
        );
        setIsSaving(true);
        hasUnsavedChangesRef.current = true; // Set this to true to allow saving
        await saveCurrentActorChanges();
        setIsSaving(false);
      }
    }
    onClose();
  };

  // Custom mask coordinates for product placement
  const getAdaptedMaskCoordinates = () => {
    if (!currentActor) return null;

    // If we have avatar data, use those coordinates
    if (currentAvatarData) {
      console.log(
        `Using avatar coordinates for product placement:`,
        currentAvatarData
      );
      return {
        x: currentAvatarData.x,
        y: currentAvatarData.y,
        width: currentAvatarData.width,
        height: currentAvatarData.height,
      };
    }

    // Fallback values if no avatar data is available
    return {
      x: 0,
      y: 0,
      width: 200, // Default width
      height: 200, // Default height
    };
  };

  // Reset coverage status when changing actors
  useEffect(() => {
    if (currentActor) {
      // Reset coverage status when changing actors
      coverageStatusRef.current = true;
      setIsProductCoveringMask(true);
    }
  }, [currentActor]);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (mergeProgressIntervalRef.current) {
        window.clearInterval(mergeProgressIntervalRef.current);
        mergeProgressIntervalRef.current = null;
      }
    };
  }, []);

  // Add state for data popup
  const [isDataPopupOpen, setIsDataPopupOpen] = useState(false);

  // Add state for tutorial dialog
  const [showTutorial, setShowTutorial] = useState(false);

  // Add state for product fill image
  const [productFillImage, setProductFillImage] = useState<string | null>(null);
  const [showProductFill, setShowProductFill] = useState(false);

  // Check if tutorial should be shown
  useEffect(() => {
    if (isOpen) {
      // Check localStorage to see if user chose not to show tutorial again
      const hideItemPositioningTutorial = localStorage.getItem(
        "hideItemPositioningTutorial"
      );
      if (hideItemPositioningTutorial !== "true") {
        setShowTutorial(true);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={isMergeLoading ? () => {} : handleDialogClose}
      className="relative z-50"
    >
      {/* Backdrop - only allow clicking when not actively generating a merge */}
      <div
        className={`fixed inset-0 bg-black/70 ${
          isMergeLoading ? "cursor-not-allowed" : ""
        }`}
        aria-hidden="true"
        onClick={isMergeLoading ? () => {} : handleDialogClose}
      />

      {/* Full-screen container for centering */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-6xl h-[92vh] bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden flex flex-col relative transition-colors duration-200">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="flex items-center">
              <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white mr-4">
                {isPreviewMode ? "Preview Product Placement" : "Place Product"}
              </Dialog.Title>
              {currentActor && (
                <div className="flex items-center">
                  <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
                    {currentActorIndex + 1} of {selectedActors.length}
                  </span>
                  {isPreviewMode && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 rounded">
                      Preview
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleDialogClose}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 hover:cursor-pointer"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-800">
            {isLoading || isTransitioning || isMaskGenerating ? (
              <div className="flex justify-center items-center h-full">
                <div className="flex flex-col items-center">
                  <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700 opacity-25"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-t-purple-500 dark:border-t-purple-400 animate-spin"></div>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 mt-4">
                    {isMaskGenerating ? "Generating mask..." : "Loading..."}
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-8 h-full flex items-center justify-center">
                <div>
                  <p className="text-red-500 dark:text-red-400 mb-4 text-lg">
                    {error}
                  </p>
                  <button
                    onClick={() => {
                      setError(null);
                      loadActorPosition(currentActorId);
                    }}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 hover:cursor-pointer text-white rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : isPreviewMode ? (
              // Preview mode
              <div className="h-full flex flex-col items-center justify-center p-4 overflow-y-auto">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Preview
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {isMergeLoading
                      ? "Generating merged image..."
                      : mergeError
                      ? "Error generating merged image"
                      : "This is how your product will appear on this character."}
                  </p>
                </div>

                {/* Display merged image if available */}
                <div className="rounded-lg overflow-hidden w-full max-w-full mb-6">
                  {isMergeLoading ? (
                    <div className="w-full h-64 bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center p-4">
                      <div className="w-full max-w-md h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-4">
                        <div
                          className="h-full bg-purple-500 dark:bg-purple-400 rounded-full transition-all duration-300"
                          style={{ width: `${mergeProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Generating merged image ({Math.round(mergeProgress)}%)
                      </p>
                    </div>
                  ) : mergeError ? (
                    <div className="w-full h-64 bg-red-50 dark:bg-red-900/20 flex flex-col items-center justify-center p-4">
                      <div className="text-red-500 dark:text-red-400 mb-2">
                        Error generating image
                      </div>
                      <p className="text-sm text-red-400 dark:text-red-300">
                        {mergeError}
                      </p>
                      <button
                        onClick={() =>
                          mergeImages(productMasks[currentActorId] as any)
                        }
                        className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 hover:cursor-pointer transition-colors"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : currentMergedImage ? (
                    <div className="flex justify-center items-center bg-gray-50 dark:bg-gray-800 p-2 md:p-4 w-full h-full">
                      <img
                        src={currentMergedImage.merged_image_url}
                        alt={`Merged image of product with character`}
                        className="w-auto h-auto max-w-full max-h-[50vh] sm:max-h-[60vh] object-contain"
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center items-center bg-gray-50 dark:bg-gray-800 p-2 md:p-4 w-full h-full">
                      <img
                        src={currentActor?.image}
                        alt={`Preview`}
                        className="w-auto h-auto max-w-full max-h-[50vh] sm:max-h-[60vh] object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : currentActor ? (
              // Positioning mode
              <div className="h-full">
                {showProductFill && productFillImage ? (
                  <div className="h-full flex flex-col items-center justify-center p-4">
                    <div className="text-center mb-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Product Fill Image
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        This is the isolated product that will be used for
                        compositing
                      </p>
                    </div>
                    <div className="flex justify-center items-center bg-gray-50 dark:bg-gray-800 p-2 md:p-4 w-full h-full max-h-[70vh]">
                      <img
                        src={productFillImage}
                        alt="Product fill image"
                        className="w-auto h-auto max-w-full max-h-[60vh] object-contain"
                      />
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => {
                          const fillImageUrl = productFillImage;
                          if (fillImageUrl) {
                            handleCopyUrl(fillImageUrl);
                          }
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 hover:cursor-pointer transition-colors flex items-center"
                      >
                        {copiedUrl === productFillImage ? (
                          <>
                            <CheckIcon className="h-4 w-4 mr-2" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                            Copy URL
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowProductFill(false)}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 hover:cursor-pointer transition-colors"
                      >
                        Back to Editor
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="w-full h-full">
                      <div className="relative h-full">
                        <PhotoEditor
                          ref={photoEditorRef}
                          baseImageUrl={transformImageUrl(currentActor.image)}
                          overlayImageUrl={transformImageUrl(
                            getProductImageUrl()
                          )}
                          initialMaskCoordinates={getAdaptedMaskCoordinates()}
                          readOnly={false}
                          initialTransform={currentProductPosition}
                          onTransformChange={handlePositionChange}
                          onCoverageChange={handleCoverageChange}
                        />
                        {/* Add a fallback loading indicator at the same position */}
                        <div
                          id="image-loading-fallback"
                          className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800 opacity-0 pointer-events-none transition-opacity duration-300"
                        >
                          <div className="relative w-16 h-16">
                            <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700 opacity-25"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-t-purple-500 dark:border-t-purple-400 animate-spin"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 h-full flex items-center justify-center">
                <p className="text-gray-500 dark:text-gray-400">
                  No actors or product image available
                </p>
              </div>
            )}
          </div>

          {/* Footer with navigation */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
            {isPreviewMode ? (
              <p className="text-sm text-gray-700 dark:text-gray-300 ml-1">
                Product or hands not looking right? Re-position your item and
                try again
              </p>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handlePrevious}
                  disabled={
                    isFirstActor ||
                    isLoading ||
                    isTransitioning ||
                    isMaskGenerating ||
                    isMergeLoading
                  }
                  className={`px-4 py-2 rounded-lg flex items-center ${
                    isFirstActor ||
                    isLoading ||
                    isTransitioning ||
                    isMaskGenerating ||
                    isMergeLoading
                      ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      : "bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-700 hover:cursor-pointer transition-colors"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Previous
                </button>
              </div>
            )}

            <div className="flex items-center">
              {isSaving && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 py-1 px-2 rounded mr-3">
                  Saving...
                </span>
              )}
              {isMaskGenerating && (
                <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 py-1 px-2 rounded mr-3">
                  Generating mask...
                </span>
              )}
              {isMergeLoading && (
                <span className="text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 py-1 px-2 rounded mr-3">
                  Merging images... ({Math.round(mergeProgress)}%)
                </span>
              )}
              {!isPreviewMode && !isProductCoveringMask ? (
                <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300 py-1 px-2 rounded mr-3">
                  Please cover the blue box completely
                </span>
              ) : (
                <span className="text-sm text-gray-800 dark:text-gray-400 hidden sm:inline">
                  {isPreviewMode
                    ? ""
                    : "Adjust position, size, and rotation as needed"}
                </span>
              )}

              {isPreviewMode ? (
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevious}
                    disabled={isLoading || isTransitioning || isMergeLoading}
                    className={`px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 hover:cursor-pointer rounded-lg transition-colors ${
                      isLoading || isTransitioning || isMergeLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Re-position
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={isLoading || isTransitioning || isMergeLoading}
                    className={`px-6 py-2 rounded-lg bg-purple-500 text-white font-medium flex items-center hover:bg-purple-600 dark:hover:bg-purple-500 hover:cursor-pointer transition-colors shadow-sm ${
                      isLoading || isTransitioning || isMergeLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isLastActor ? "Finish" : "Confirm"}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 ml-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSkip}
                    disabled={
                      isLoading ||
                      isTransitioning ||
                      isMaskGenerating ||
                      isMergeLoading
                    }
                    className={`px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 hover:cursor-pointer rounded-lg transition-colors ${
                      isLoading ||
                      isTransitioning ||
                      isMaskGenerating ||
                      isMergeLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Skip
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={
                      isLoading ||
                      isTransitioning ||
                      isMaskGenerating ||
                      isMergeLoading
                    }
                    className={`px-6 py-2 rounded-lg bg-purple-500 text-white font-medium flex items-center hover:bg-purple-600 hover:cursor-pointer transition-colors shadow-sm ${
                      isLoading ||
                      isTransitioning ||
                      isMaskGenerating ||
                      isMergeLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Preview
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 ml-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>

      {/* Tutorial Dialog */}
      <TutorialDialog
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        videoUrl="/demo-item-positioning.mp4"
        title="How to Position Items"
      />

      {/* Data Detail Popup */}
      {isDataPopupOpen && (
        <Dialog
          open={isDataPopupOpen}
          onClose={() => setIsDataPopupOpen(false)}
          className="relative z-[60]"
        >
          <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                  Technical Details
                </Dialog.Title>
                <button
                  onClick={() => setIsDataPopupOpen(false)}
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 hover:cursor-pointer transition-colors duration-200"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="p-4">
                <div className="flex flex-col gap-3">
                  {/* Character ID */}
                  <div className="flex justify-between items-center">
                    <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px]">
                      Character ID:
                    </div>
                    <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                      {currentActorId}
                      <button
                        onClick={() => handleCopyUrl(currentActorId)}
                        className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedUrl === currentActorId ? (
                          <CheckIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Project ID */}
                  <div className="flex justify-between items-center">
                    <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px]">
                      Project ID:
                    </div>
                    <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                      {projectId}
                      <button
                        onClick={() => handleCopyUrl(projectId)}
                        className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedUrl === projectId ? (
                          <CheckIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Avatar Image URL */}
                  <div className="flex justify-between items-start">
                    <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px] pt-2">
                      Avatar Image URL:
                    </div>
                    <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                      {currentActor?.image}
                      <button
                        onClick={() => handleCopyUrl(currentActor?.image || "")}
                        className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedUrl === currentActor?.image ? (
                          <CheckIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Product Image URL */}
                  <div className="flex justify-between items-start">
                    <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px] pt-2">
                      Product Image URL:
                    </div>
                    <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                      {getProductImageUrl() || "No product image"}
                      {getProductImageUrl() && (
                        <button
                          onClick={() =>
                            handleCopyUrl(getProductImageUrl() || "")
                          }
                          className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedUrl === getProductImageUrl() ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Generated Mask URL */}
                  <div className="flex justify-between items-start">
                    <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px] pt-2">
                      Generated Mask URL:
                    </div>
                    <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                      {currentMaskData
                        ? currentMaskData.mask_url
                        : "No mask generated"}
                      {currentMaskData && (
                        <button
                          onClick={() =>
                            handleCopyUrl(currentMaskData.mask_url)
                          }
                          className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedUrl === currentMaskData.mask_url ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mask ID */}
                  <div className="flex justify-between items-center">
                    <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px]">
                      Mask ID:
                    </div>
                    <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                      {currentMaskData ? currentMaskData.mask_id : "No mask ID"}
                      {currentMaskData && (
                        <button
                          onClick={() => handleCopyUrl(currentMaskData.mask_id)}
                          className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedUrl === currentMaskData.mask_id ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Merged Image URL (only if available) */}
                  {currentMergedImage && (
                    <div className="flex justify-between items-start">
                      <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px] pt-2">
                        Merged Image URL:
                      </div>
                      <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                        {currentMergedImage.merged_image_url}
                        <button
                          onClick={() =>
                            handleCopyUrl(currentMergedImage.merged_image_url)
                          }
                          className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedUrl === currentMergedImage.merged_image_url ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Merged Image ID (only if available) */}
                  {currentMergedImage && (
                    <div className="flex justify-between items-center">
                      <div className="text-gray-500 dark:text-gray-400 font-semibold min-w-[160px]">
                        Merged Image ID:
                      </div>
                      <div className="text-gray-900 dark:text-gray-200 flex-1 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all">
                        {currentMergedImage.merged_image_id}
                        <button
                          onClick={() =>
                            handleCopyUrl(currentMergedImage.merged_image_id)
                          }
                          className="ml-2 inline-flex items-center text-purple-500 hover:text-purple-700 hover:cursor-pointer transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedUrl === currentMergedImage.merged_image_id ? (
                            <CheckIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex justify-end">
                <button
                  onClick={() => setIsDataPopupOpen(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 hover:cursor-pointer transition-colors"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}
